import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useT, EventEmitter, hasValue } from '@progress-fe/core';
import { Box, Center, Flex, Grid, Button, GridItem, VStack, Divider } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Dialog } from '../../base';
import { InputNumber } from '../../inputs';
import { useJsFieldFormData } from '../../../hooks';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldError, JsFieldInspector } from '../../jsFormBase';
import { IconButton } from '../../buttons';
import { ReactionsList } from '../../lists';
/**
 * Component renders "/schemas/jsf-reaction-set-composition" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TReactionCompositionFormData.
 */
const ReactionSetCompositionJsFieldFC = (props) => {
    const [isDialog, setIsDialog] = useState(false);
    const { reactions = {}, isInspector } = useContext(JsFormConfigContext) ?? {};
    const { list: reactionsList = [], dictionary = {} } = reactions;
    const { t } = useT();
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: props.formData?.fieldConfig || null,
        values: props.formData?.fieldValues || null,
        onChange: props.onChange
    });
    useEffect(() => {
        onChangeFieldConfig(props.formData?.fieldConfig);
    }, [props.formData?.fieldConfig, onChangeFieldConfig]);
    const selectedReactions = useMemo(() => fieldValues || [], [fieldValues]);
    const selectedSet = useMemo(() => new Set(selectedReactions.map((item) => item.reactionId)), [selectedReactions]);
    const onChangeFormValue = (value) => {
        if (fieldValues) {
            onChangeFieldValues(value);
        }
    };
    const handleChangeValue = (reactionId, value) => {
        if (!fieldValues) {
            return;
        }
        const newVal = fieldValues.map((item) => item.reactionId === reactionId ? { ...item, ...value } : item);
        if (fieldConfig?.isRankable) {
            newVal.sort((a, b) => (a?.rank || 0) - (b?.rank || 0));
        }
        onChangeFormValue(newVal);
    };
    const handleAddReaction = (reactionId) => {
        if (!fieldValues) {
            return;
        }
        const newVal = [...fieldValues, { reactionId, rank: selectedReactions.length + 1 }];
        onChangeFormValue(newVal);
    };
    const handleDeleteReaction = (reactionId) => {
        if (!fieldValues) {
            return;
        }
        const filtered = fieldValues.filter((item) => item.reactionId !== reactionId);
        onChangeFormValue(filtered);
    };
    const handleGoToReaction = (reaction) => {
        EventEmitter.emit('SelectItem', reaction.reactionId);
    };
    if (!fieldConfig || !fieldValues) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    const hasReactions = fieldValues.length;
    const isReadOnly = !!props.schema.readOnly;
    const gridTemplate = fieldConfig.isRankable ? '1fr 3fr 24px 24px' : '1fr 24px 24px';
    return (_jsxs(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: [_jsxs(VStack, { "data-testid": "ReactionSetCompositionJsField-test", "data-name": props.name, p: "0 0 2px 0", gap: "8px", width: "100%", height: "100%", align: 'stretch', children: [hasReactions ? (_jsxs(Flex, { flexDirection: 'column', p: "4px", borderRadius: "4px", border: "1px solid", borderColor: "border", gap: '4px', children: [_jsxs(Grid, { gap: "4px", alignItems: "center", templateColumns: gridTemplate, children: [_jsx(GridItem, { children: t('fields.reaction') }), fieldConfig.isRankable && _jsx(GridItem, { children: t('fields.rank') }), _jsx(GridItem, {}), _jsx(GridItem, {})] }), _jsx(Divider, {}), selectedReactions.map((reaction) => (_jsxs(Grid, { gap: "4px", templateColumns: gridTemplate, children: [_jsx(Flex, { align: "center", children: dictionary[reaction.reactionId]?.name }), fieldConfig.isRankable && (_jsx(InputNumber, { isFloat: true, min: 0, size: "xs", variant: "outline", sx: { width: '100%' }, isOnChangeOnlyOnBlur: true, disabled: isReadOnly, isInvalid: !hasValue(reaction.rank), value: reaction.rank, onChange: (value) => handleChangeValue(reaction.reactionId, { rank: Number(value) }) })), _jsx(IconButton, { isDisabled: isReadOnly, icon: "Cross", onClick: () => handleDeleteReaction(reaction.reactionId) }), _jsx(IconButton, { isDisabled: isReadOnly, icon: _jsx(ChevronRightIcon, { boxSize: "20px" }), onClick: () => handleGoToReaction(reaction) })] }, reaction.reactionId)))] })) : (_jsx(Center, { w: "100%", h: "64px", border: "1px solid", borderColor: "border", borderRadius: "4px", children: _jsx(Box, { opacity: "0.6", children: t('reactions.no') }) })), !props.schema.readOnly && (_jsx(Button, { variant: "ghost", size: "smSquare", width: "100%", onClick: () => setIsDialog(true), isDisabled: isReadOnly, children: t('reactions.add') }))] }), isDialog && (_jsx(Dialog, { title: t('reactions.add'), onClose: () => setIsDialog(false), children: _jsx(ReactionsList, { tableSx: { height: 300, width: 300 }, reactions: reactionsList, selectedReactions: selectedSet, onAddReaction: handleAddReaction, onDeleteReaction: handleDeleteReaction }) }))] }));
};
export const ReactionSetCompositionJsField = memo(ReactionSetCompositionJsFieldFC);
//# sourceMappingURL=ReactionSetCompositionJsField.js.map